import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ResponsiveNavigation from '../../components/ResponsiveNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, TrendingUp, Receipt, Building2, Briefcase, Download } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { PolishedKpiTile, GranularityTabs, SectionEyebrow, bucketLabelFor } from '../../components/AnalyticsWidgets';
import { MetricDetailModal } from '../../components/MetricDetailModal';
import { downloadAdminAnalyticsPdf } from '../../utils/analyticsPdf';

const formatMoney = (cents, currency = 'EUR') => {
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format((cents || 0) / 100); }
  catch { return `${Math.round((cents || 0) / 100)} ${currency}`; }
};

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [overview, setOverview] = useState(null);
  const [dashboardKpis, setDashboardKpis] = useState(null);
  const [granularity, setGranularity] = useState('month');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [activeMetric, setActiveMetric] = useState(null);

  const fetchAll = useCallback(async (g) => {
    setLoading(true);
    try {
      const [d, o] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/dashboard`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/overview?granularity=${g}`),
      ]);
      setDashboardKpis(d.data?.data || null);
      setOverview(o.data?.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(granularity); }, [granularity, fetchAll]);

  const labelFor = useMemo(() => bucketLabelFor(granularity), [granularity]);

  const chartData = useMemo(() => {
    if (!overview) return [];
    return overview.series.map((p) => ({
      bucket: labelFor(p.bucket),
      licenses: p.licenses,
      turnover: Math.round((p.turnoverCents || 0) / 100),
      licenseSales: p.licenseSales,
      newCompanies: p.newCompanies,
      applications: p.applications,
      prospects: p.prospects,
    }));
  }, [overview, labelFor]);

  const downloadPdf = async () => {
    if (!overview || !dashboardKpis) return;
    setDownloading(true);
    try {
      await downloadAdminAnalyticsPdf({
        overview, dashboard: dashboardKpis, granularity,
        chartElementIds: ['chart-licenses-turnover', 'chart-licenses-over-time', 'chart-sales-made-companies', 'chart-funnel'],
      });
    } finally { setDownloading(false); }
  };

  if (!userProfile) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation userProfile={userProfile} />
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Back" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-sm text-gray-500">Live platform-wide metrics — pulled directly from the database</p>
          </div>
          <div className="flex items-center gap-3">
            <GranularityTabs value={granularity} onChange={setGranularity} />
            <button
              onClick={downloadPdf}
              disabled={downloading || loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 rounded-lg transition-colors"
              data-testid="download-analytics-pdf"
            >
              <Download className="w-3.5 h-3.5" /> {downloading ? 'Building…' : 'Download PDF'}
            </button>
          </div>
        </div>

        {loading || !overview || !dashboardKpis ? (
          <p className="text-sm text-gray-500" data-testid="analytics-loading">Loading analytics…</p>
        ) : (
          <>
            {/* Hero KPIs — 4 large tiles, this period vs previous */}
            <SectionEyebrow
              title="Headline metrics"
              subtitle={`Current ${granularity} vs previous ${granularity} · click any tile for the full history`}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <PolishedKpiTile
                size="lg" icon={Briefcase} label="Revenue"
                value={formatMoney(dashboardKpis.turnoverCents, dashboardKpis.currency)}
                testId="kpi-month-turnover"
                series={overview.series.map(s => ({ bucket: s.bucket, value: Math.round((s.turnoverCents || 0) / 100) }))}
                bucketLabel={labelFor}
                onClick={() => setActiveMetric({
                  icon: Briefcase, label: 'Revenue',
                  currentValue: formatMoney(dashboardKpis.turnoverCents, dashboardKpis.currency),
                  series: overview.series.map(s => ({ bucket: s.bucket, value: Math.round((s.turnoverCents || 0) / 100) })),
                  formatValue: (v) => formatMoney(v * 100, dashboardKpis.currency),
                })}
              />
              <PolishedKpiTile
                size="lg" icon={TrendingUp} label="Licenses sold (seats)"
                value={dashboardKpis.licensesSoldThisMonth}
                testId="kpi-month-licenses"
                series={overview.series.map(s => ({ bucket: s.bucket, value: s.licenses }))}
                bucketLabel={labelFor}
                onClick={() => setActiveMetric({
                  icon: TrendingUp, label: 'Licenses sold (seats)',
                  currentValue: dashboardKpis.licensesSoldThisMonth,
                  series: overview.series.map(s => ({ bucket: s.bucket, value: s.licenses })),
                })}
              />
              <PolishedKpiTile
                size="lg" icon={Receipt} label="Sales made"
                value={dashboardKpis.licenseSalesThisMonth}
                testId="kpi-month-sales-made"
                series={overview.series.map(s => ({ bucket: s.bucket, value: s.licenseSales }))}
                bucketLabel={labelFor}
                onClick={() => setActiveMetric({
                  icon: Receipt, label: 'Sales made',
                  currentValue: dashboardKpis.licenseSalesThisMonth,
                  series: overview.series.map(s => ({ bucket: s.bucket, value: s.licenseSales })),
                })}
              />
              <PolishedKpiTile
                size="lg" icon={Building2} label="New companies"
                value={dashboardKpis.newCompaniesThisMonth}
                testId="kpi-month-companies"
                series={overview.series.map(s => ({ bucket: s.bucket, value: s.newCompanies }))}
                bucketLabel={labelFor}
                onClick={() => setActiveMetric({
                  icon: Building2, label: 'New companies',
                  currentValue: dashboardKpis.newCompaniesThisMonth,
                  series: overview.series.map(s => ({ bucket: s.bucket, value: s.newCompanies })),
                })}
              />
            </div>

            {/* Primary chart — Revenue trend */}
            <SectionEyebrow title="Revenue trend" subtitle={`Total ${formatMoney(overview.totals.turnoverCents, overview.currency)} across the last ${overview.periods} ${granularity === 'month' ? 'months' : granularity === 'quarter' ? 'quarters' : 'years'}`} />
            <Card className="mb-8">
              <CardContent className="pt-6">
                <div id="chart-licenses-turnover" data-pdf-title="Revenue trend" className="h-72 min-h-[240px]" data-testid="chart-licenses-turnover">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v} ${overview.currency}`} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} formatter={(value) => [`${value} ${overview.currency}`, 'Revenue']} />
                      <Bar dataKey="turnover" name="Revenue" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={44} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Secondary charts — 2 columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-gray-900 tracking-tight">Licenses sold over time</CardTitle>
                  <p className="text-xs text-gray-500">Seats added — new customers &amp; top-ups combined</p>
                </CardHeader>
                <CardContent>
                  <div id="chart-licenses-over-time" data-pdf-title="Licenses sold over time" className="h-56 min-h-[200px]" data-testid="chart-licenses-over-time">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        <Bar dataKey="licenses" name="Licenses" fill="#60a5fa" radius={[6, 6, 0, 0]} maxBarSize={32} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-gray-900 tracking-tight">Sales made &amp; new companies</CardTitle>
                  <p className="text-xs text-gray-500">Each license sale (new customer + top-ups) and new customer sign-ups</p>
                </CardHeader>
                <CardContent>
                  <div id="chart-sales-made-companies" data-pdf-title="Sales made & new companies" className="h-56 min-h-[200px]" data-testid="chart-sales-made-companies">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                        <Line type="monotone" dataKey="licenseSales" name="Sales made" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3, fill: '#3b82f6' }} />
                        <Line type="monotone" dataKey="newCompanies" name="New companies" stroke="#94a3b8" strokeWidth={2.5} dot={{ r: 3, fill: '#94a3b8' }} strokeDasharray="4 4" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bottom row — Top revenue table (full width) */}
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-gray-900 tracking-tight">Top revenue customers</CardTitle>
                  <p className="text-xs text-gray-500">Cumulative in the current window · click a row to open the company</p>
                </CardHeader>
                <CardContent>
                  {overview.topRevenue.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">No revenue recorded yet.</p>
                  ) : (
                    <table className="w-full text-sm" data-testid="table-top-revenue">
                      <tbody>
                        {overview.topRevenue.map((r, idx) => (
                          <tr key={r.orgId} className="border-t border-gray-100 first:border-t-0 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/admin/companies/${r.orgId}`)}>
                            <td className="py-3 pr-2 w-6 text-xs text-gray-400 tabular-nums">{idx + 1}</td>
                            <td className="py-3 px-2 text-gray-900 font-medium">{r.name}</td>
                            <td className="py-3 pl-2 text-right text-gray-900 font-semibold tabular-nums">{formatMoney(r.cents, overview.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 text-[11px] text-gray-500">
                    <span>Active positions</span>
                    <span className="text-gray-900 font-semibold tabular-nums" data-testid="kpi-active-positions">{overview.activePositions} <span className="text-gray-400 font-normal">of {overview.totalPositions}</span></span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Metric detail modal */}
      <MetricDetailModal
        isOpen={!!activeMetric}
        onClose={() => setActiveMetric(null)}
        icon={activeMetric?.icon}
        label={activeMetric?.label}
        currentValue={activeMetric?.currentValue}
        series={activeMetric?.series || []}
        bucketLabel={labelFor}
        formatValue={activeMetric?.formatValue}
        granularity={granularity}
      />
    </div>
  );
};

export default AdminAnalytics;
